import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';

// Normaliza texto para busca por conteúdo: remove acentos e caixa.
const normalizeSearchText = (text) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Monta o texto pesquisável de uma opção, incluindo conteúdo extra (código, CNPJ, etc.).
const buildOptionSearchText = (option) => {
  const parts = [option.label, option.searchText];
  if (Array.isArray(option.keywords)) parts.push(...option.keywords);
  return normalizeSearchText(parts.filter(Boolean).join(' '));
};

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Selecione...", 
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  // Encontrar o label do valor selecionado
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  
  useEffect(() => {
    // Fechar ao clicar fora
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  const filteredOptions = options.filter((opt) =>
    buildOptionSearchText(opt).includes(normalizedSearchTerm)
  );
  const MAX_VISIBLE_RESULTS = 100;
  const visibleOptions = filteredOptions.slice(0, MAX_VISIBLE_RESULTS);
  const hiddenResultsCount = filteredOptions.length - visibleOptions.length;

  const handleSelect = (val) => {
    onChange({ target: { value: val } }); // Simular evento padrão para compatibilidade
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <div 
        className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white flex items-center justify-between cursor-pointer ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`block truncate ${!selectedOption ? 'text-gray-400' : 'text-gray-900'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          {visibleOptions.length > 0 ? (
            <>
              {visibleOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 ${String(value) === String(opt.value) ? 'bg-blue-100 text-blue-700' : 'text-gray-900'}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </div>
              ))}
              {hiddenResultsCount > 0 && (
                <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                  Mais {hiddenResultsCount} resultados. Refine a busca para ver outros.
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500">Nenhum resultado encontrado</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
